import React, { useEffect, useState, useRef, useContext, useMemo, useCallback } from 'react'
import { Tooltip, IconButton, Divider } from '@mui/material'
import { Search, Star, StarOutline } from '@mui/icons-material'
import { Paginator } from 'primereact/paginator'
import { M_InputTextField, M_ElementDrawer, M_Table, M_SimpleTable, BufferService, filtersSortService, LoadingContext } from '../../M_Components'
import { useTranslation } from 'react-i18next'
import { useFavorites, useRegisterFavorite } from '../M_FormFavorites/FavoritesContext'

export function M_DrawerTableTextField({
    label, value, entity, filterField, fieldType, tableLabel, color,
    secondColor, FILTER, required, tooltipAdd, disabled, labelFields,
    onSubmitValue, size, icon, style, disabledColor, margin, translate, loadOnMount,
    onSaveFavorite, onRemoveFavorite, fieldFavorites, favoriteOptions,
    favoritesFieldName, favoritesGroupName, favoritesIdKey, favoritesVarKey, api,
    staticOptions, staticTableColumns, onDrawerOpen, onSearchInput, filtersOnTable,
    drawerUseEntityTable, hiddenColumns, drawerSearchValue, onDrawerSearchChange,
    drawerPage = 1, drawerPageSize = 25, drawerTotalItems = 0, onDrawerPageChange,
    useGet = false }) {

    useRegisterFavorite({ label, favoritesFieldName, favoritesGroupName, favoritesIdKey, favoritesVarKey, required})

    const { t } = useTranslation()
    const [valueInput, setValueInput] = useState(value)
    const [onOpen, setOnOpen] = useState(false)
    const [options, setOptions] = useState([])
    const allGetOptions = useRef([])

    const headerSize = document.body.getElementsByClassName('p-datatable-header')[0]?.offsetHeight || 60
    const paginatorSize = document.body.getElementsByClassName('paginator')[0]?.offsetHeight || 60
    const tabSize = 64
    const tempSize = (document.body.offsetHeight - (headerSize + paginatorSize + tabSize + 5))

    const useStaticOptions = Array.isArray(staticOptions)
    const entityService = useMemo(() => {
        if (useStaticOptions) {
            return null
        }

        return new BufferService(entity, api === 'secondary' ? 'secondary' : null)
    }, [useStaticOptions, entity, api])
    const filterService = new filtersSortService()
    const drawerLookupPageSize = 15
    const buildEntityQueryUrl = (url) => `/query${url}`

    const ctx = useContext(LoadingContext)

    color = color ? color : ctx.primaryColor ? ctx.primaryColor : "var(--color-black)"

    useEffect(() => {
        if (value !== valueInput) {
            setValueInput(value)
        }
    })

    useEffect(() => {
        if (loadOnMount) {
            if (useStaticOptions) {
                setOptions(staticOptions)
                return
            }

            const fetchInitialData = () => {
                const initialFilter = filterService.queryBuilder(1, drawerLookupPageSize, null, null, FILTER);

                entityService.filter(buildEntityQueryUrl(initialFilter)).then(res => {
                    if (res.name === 'AxiosError') {
                        return;
                    }
                    setOptions(res.Items);
                });
            };

            fetchInitialData();
        }
    }, [loadOnMount, entity, FILTER, useStaticOptions, staticOptions]);

    useEffect(() => {
        if (useStaticOptions) {
            setOptions(staticOptions || [])
            return
        }

        setOptions([])
    }, [FILTER, useStaticOptions, staticOptions])


    const openDrawer = () => {
        if (onDrawerOpen) {
            onDrawerOpen()
        }
        setOnOpen(true)
    }

    const closeDrawer = () => {
        setOnOpen(false)
    }

    const keyDown = (e) => {
        if (e.key === 'F4') {
            openDrawer()
        }
    }

    const getLine = (e) => {
        closeDrawer()
        onChangeValue(null, e[0], 'changevalue')
    }

    const getStaticLine = (e) => {
        if (!e?.value) {
            return
        }

        closeDrawer()
        onChangeValue(null, e.value, 'changevalue')
    }

    const returnTable = () => {
        if (onOpen) {
            if (useStaticOptions) {
                if (drawerUseEntityTable && entity) {
                    const tableColumns = staticTableColumns || labelFields?.map((field) => ({
                        field,
                        header: field,
                    })) || []

                    return (
                        <>
                            <h2 style={{ marginLeft: '10px', marginTop: '0px', marginBottom: '10px' }}>{tableLabel}</h2>
                            <div style={{ margin: '0 10px 10px 10px' }}>
                                <M_SimpleTable
                                    rows={options}
                                    columns={tableColumns}
                                    selectedRows={valueInput}
                                    onSelectionChange={getStaticLine}
                                    isSelectSingle
                                    limitHeight={`${tempSize}px`}
                                    filtersOnTable={filtersOnTable ?? true}
                                />
                                {onDrawerPageChange && (
                                    <Paginator
                                        first={(Math.max(drawerPage, 1) - 1) * drawerPageSize}
                                        rows={drawerPageSize}
                                        totalRecords={drawerTotalItems}
                                        onPageChange={(e) => onDrawerPageChange(Math.floor(e.first / e.rows) + 1, e.rows)}
                                    />
                                )}
                            </div>
                        </>
                    )
                }

                const tableColumns = staticTableColumns || labelFields?.map((field) => ({
                    field,
                    header: field,
                })) || []

                return (
                    <>
                        <h2 style={{ marginLeft: '10px', marginTop: '0px', marginBottom: '10px' }}>{tableLabel}</h2>
                        <div style={{ margin: '0 10px 10px 10px' }}>
                            <M_SimpleTable
                                rows={options}
                                columns={tableColumns}
                                selectedRows={valueInput}
                                onSelectionChange={getStaticLine}
                                isSelectSingle
                                limitHeight={`${tempSize}px`}
                                filtersOnTable={filtersOnTable}
                            />
                        </div>
                    </>
                )
            }

            return (
                <>
                    <h2 style={{ marginLeft: '10px', marginTop: '0px', marginBottom: '10px' }}>{tableLabel}</h2>
                    <M_Table
                        api={api}
                        tableHeight={tempSize}
                        entityName={entity}
                        useSessionStorage={false}
                        crud={false}
                        onPassLine={getLine}
                        selectSingle={true}
                        FILTER={FILTER}
                        hiddenColumns={hiddenColumns}
                        initialPageSize={drawerLookupPageSize}
                        useQueryEndpoint={!useGet}
                    />
                </>
            )
        }
    }

    const getValues = (input) => {
        const tempInput = input.trim()
        if (useStaticOptions) {
            if (onSearchInput) {
                onSearchInput(tempInput)
                return
            }

            if (!tempInput) {
                setOptions(staticOptions || [])
                return
            }

            const fieldsToFilter = [...new Set([filterField, ...(labelFields || [])].filter(Boolean))]
            const filteredOptions = (staticOptions || []).filter((option) =>
                fieldsToFilter.some((field) =>
                    `${option?.[field] ?? ''}`.toLowerCase().includes(tempInput.toLowerCase())
                )
            )

            setOptions(filteredOptions)
            return
        }

        if (useGet) {
            const all = allGetOptions.current
            if (!tempInput) {
                setOptions(all)
                return
            }
            const fieldsToFilter = [...new Set([filterField, ...(labelFields || [])].filter(Boolean))]
            setOptions(all.filter((option) =>
                fieldsToFilter.some((field) =>
                    `${option?.[field] ?? ''}`.toLowerCase().includes(tempInput.toLowerCase())
                )
            ))
            return
        }

        if (filterField) {
            const tempArg = [{ name: filterField, type: fieldType, operation: 'Contains', value: tempInput }]
            const tempFilter = filterService.queryBuilder(1, drawerLookupPageSize, null, tempArg, FILTER)
            entityService.filter(buildEntityQueryUrl(tempFilter)).then(res => {
                if (res.name === 'AxiosError') {
                    return
                }
                setOptions(res.Items)
            })
        }
    }

    const handleDropdownOpen = () => {
        if (useStaticOptions) {
            if (onSearchInput) {
                onSearchInput('')
                return
            }

            setOptions(staticOptions || [])
            return
        }

        if (useGet) {
            if (allGetOptions.current.length === 0) {
                entityService.get().then(res => {
                    if (res?.name === 'AxiosError') return
                    const items = res?.Items ?? (Array.isArray(res) ? res : [])
                    allGetOptions.current = items
                    setOptions(items)
                })
            }
            return
        }

        if (options.length === 0 || FILTER) {
            const tempArg = filterField
                ? [{ name: filterField, type: fieldType, operation: 'Contains', value: '' }]
                : null
            const initialFilter = filterService.queryBuilder(1, drawerLookupPageSize, null, tempArg, FILTER)
            entityService.filter(buildEntityQueryUrl(initialFilter)).then(res => {
                if (res.name === 'AxiosError') return
                setOptions(res.Items)
            })
        }
    }

    const onChangeValue = (event, value, reason) => {
        if (reason === 'clear') {
            setValueInput('')
            onSubmitValue(null)
            return
        }
        setValueInput(value)
        onSubmitValue(value)
    }

    const clickOption = (option) => {
        getLine([option])
    }

    // FavoritesContext support
    const favCtx = useFavorites()

    const ctxFieldFavorites = favoritesFieldName && favCtx ? favCtx.getFieldFavoriteValues(favoritesFieldName) : null
    const ctxFieldFavoriteItems = favoritesFieldName && favCtx?.getFieldFavoriteData ? favCtx.getFieldFavoriteData(favoritesFieldName, labelFields) : null
    const ctxSaveFavorite = useCallback((obj) => {
        if (!favCtx || !favoritesFieldName) return
        const val = obj?.Id || obj
        const lbl = labelFields ? labelFields.map(f => obj?.[f] || '').join(' ').trim() : ''
        const idVal = favoritesIdKey ? val : null
        // Pass full option object as optionData for reconstruction
        const optionData = obj && typeof obj === 'object' ? obj : null
        favCtx.saveFieldFavoriteAuto(favoritesFieldName, val, lbl, favoritesGroupName || null, favoritesIdKey || null, idVal, favoritesVarKey || favoritesFieldName, optionData)
    }, [favCtx, favoritesFieldName, favoritesGroupName, favoritesIdKey, favoritesVarKey, labelFields])
    const ctxRemoveFavorite = useCallback((val) => {
        if (!favCtx || !favoritesFieldName) return
        favCtx.removeFieldFavorite(favoritesFieldName, val)
    }, [favCtx, favoritesFieldName])

    const effectiveSaveFavorite = onSaveFavorite || (favoritesFieldName && favCtx ? ctxSaveFavorite : null)
    const effectiveRemoveFavorite = onRemoveFavorite || (favoritesFieldName && favCtx ? ctxRemoveFavorite : null)
    const effectiveFieldFavorites = fieldFavorites || ctxFieldFavorites || []

    const hasFavorites = !!effectiveSaveFavorite
    const favoriteValues = effectiveFieldFavorites

    // Use favorite items from context metadata
    const effectiveFavoriteItems = favoriteOptions || (ctxFieldFavoriteItems?.length > 0 ? ctxFieldFavoriteItems : null)

    const isCurrentValueFavorite = useMemo(() => {
        if (!hasFavorites || !valueInput) return false
        const val = valueInput?.Id || valueInput
        return favoriteValues.some(fv => String(fv).toLowerCase() === String(val).toLowerCase())
    }, [hasFavorites, valueInput, favoriteValues])

    const handleStarClick = (e) => {
        e.stopPropagation()
        if (!valueInput) return
        if (isCurrentValueFavorite) {
            if (effectiveRemoveFavorite) {
                const val = valueInput?.Id || valueInput
                effectiveRemoveFavorite(val)
            }
        } else {
            if (effectiveSaveFavorite) {
                effectiveSaveFavorite(valueInput)
            }
        }
    }

    const starButton = hasFavorites ? (
        <IconButton
            className="fav-star-btn"
            size="small"
            onClick={handleStarClick}
            onMouseDown={(e) => { e.stopPropagation(); e.preventDefault() }}
            sx={{ padding: '2px' }}
            disabled={!valueInput}
        >
            {isCurrentValueFavorite ?
                <Star sx={{ color: 'var(--color-yellow, #f9a825)', fontSize: '20px' }} /> :
                <StarOutline sx={{ color: '#bbb', fontSize: '20px' }} />
            }
        </IconButton>
    ) : null

    // Merge favorite items into options, favorites first
    const mergedOptions = useMemo(() => {
        if (!hasFavorites || !effectiveFavoriteItems || effectiveFavoriteItems.length === 0) return options
        const favIds = effectiveFavoriteItems.map(o => String(o.Id).toLowerCase())
        const rest = options.filter(o => !favIds.includes(String(o.Id).toLowerCase()))
        const favs = effectiveFavoriteItems.map((o, i) => ({ ...o, _isFavorite: true, ...(i === 0 ? { _firstFavorite: true } : {}) }))
        if (rest.length === 0) return favs
        return [...favs, { ...rest[0], _dividerBefore: true }, ...rest.slice(1)]
    }, [options, effectiveFavoriteItems, hasFavorites])

    // Always include the current value in options so MUI finds it even before async options load
    const effectiveOptions = useMemo(() => {
        if (!valueInput || typeof valueInput !== 'object' || !valueInput.Id) return mergedOptions
        const alreadyIn = mergedOptions.some(o => String(o.Id).toLowerCase() === String(valueInput.Id).toLowerCase())
        if (alreadyIn) return mergedOptions
        return [valueInput, ...mergedOptions]
    }, [mergedOptions, valueInput])

    const favoriteRenderOption = hasFavorites && effectiveFavoriteItems?.length > 0 ? (props, option) => {
        const { key, ...restProps } = props
        let optionLabel = ''
        labelFields?.forEach(obj => {
            optionLabel = optionLabel.concat(` ${option[obj] || ''}`)

        })
        return (
            <div key={key}>
                {option._firstFavorite && <li style={{ padding: '4px 16px', fontSize: '12px', fontWeight: 600, color: 'var(--color-black)', pointerEvents: 'none' }}>{t('Favorites')}</li>}
                {option._dividerBefore && <>
                    <Divider sx={{ my: '4px' }} />
                    <li style={{ padding: '4px 16px', fontSize: '12px', fontWeight: 600, color: 'var(--color-black)', pointerEvents: 'none' }}>{t('All')}</li>
                </>}
                <li {...restProps}>
                    {option._isFavorite && <Star sx={{ color: 'var(--color-yellow, #f9a825)', fontSize: '16px', marginRight: '6px' }} />}
                    {optionLabel}
                </li>
            </div>
        )
    } : undefined
    return (
        <>
            <M_InputTextField
                autocomplete
                options={effectiveOptions}
                disabled={disabled}
                label={translate && t(label) || label}
                value={valueInput}
                onClickOption={clickOption}
                labelFields={labelFields}
                onChangeValue={onChangeValue}
                onChange={(e) => getValues(e.target.value)}
                onKeyDown={(e) => keyDown(e)}
                color={color}
                secondColor={secondColor}
                required={required}
                size={size ? size : '100%'}
                style={style}
                disabledColor={disabledColor}
                renderOption={favoriteRenderOption}
                endAdornment={starButton}
                onOpen={handleDropdownOpen}
                filterOptions={(x) => x}
                // margin={margin}
                InputProps={{
                    startAdornment: (
                        disabled ? <></> :
                            <Tooltip style={{ color: color, cursor: 'pointer' }} title={tooltipAdd ? tooltipAdd : ""} onClick={() => openDrawer()}>
                                {icon ? icon :
                                    <Search />
                                }
                            </Tooltip>
                    ),
                }}
            />
            <M_ElementDrawer
                open={onOpen}
                setOpen={setOnOpen}
                onClose={closeDrawer}
                element={returnTable()}
            />
        </>
    )
}